import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonVariant } from "@industriousoffice/react-components";
import type { Location } from "@/api/opb/opbTypes";
import {
  getLocationEmail,
  getOfflineDirectDebitForm,
} from "../utils/offline-direct-debit";

export function OfflineDirectDebitPanel({
  location,
}: {
  location: Location;
}): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const form = getOfflineDirectDebitForm(location);
  const email = getLocationEmail(location);
  const formUrl =
    form == null ? undefined : `${import.meta.env.BASE_URL}${form.assetPath}`;

  const download = async () => {
    if (form == null || formUrl == null || isDownloading) return;
    setIsDownloading(true);
    setDownloadError(false);
    try {
      const response = await fetch(formUrl);
      if (
        !response.ok ||
        !response.headers.get("content-type")?.includes("application/pdf")
      ) {
        throw new Error("Form unavailable");
      }
      const file = await response.blob();
      const fileUrl = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = form.filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } catch {
      setDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section
      className="payment-panel offline-panel"
      aria-labelledby="offline-heading"
    >
      <div className="payment-panel-heading">
        <span className="payment-section-icon" aria-hidden="true">
          <ArrowDownTrayIcon width={20} height={20} />
        </span>
        <h2 id="offline-heading">{t("Direct Debit")}</h2>
        <span className="payment-status">{t("Set up with your location")}</span>
      </div>
      <p>
        {t("Arrange Direct Debit with your location team using a signed form.")}
      </p>
      <ol className="offline-steps">
        <li>
          <strong>{t("Download the form")}</strong>
          <span>{location.country} · PDF</span>
          {form == null && (
            <p className="payment-notice" role="status">
              {t(
                "The approved form is not available yet. Please contact your location team for help setting up Direct Debit."
              )}
            </p>
          )}
          {downloadError && (
            <p className="payment-error" role="alert">
              {t(
                "We couldn’t download the form. Please try again or contact your location team."
              )}
            </p>
          )}
          <div className="payment-panel-actions offline-step-actions">
            <Button
              type="button"
              isDisabled={form == null}
              isLoading={isDownloading}
              onClick={download}
            >
              {t(downloadError ? "Try again" : "Download form")}
            </Button>
            {formUrl != null && (
              <a
                className="payment-link"
                href={formUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t("View form")}
                <span className="sr-only"> {t("(opens in a new tab)")}</span>
              </a>
            )}
            {formUrl == null && (
              <Button type="button" variant={ButtonVariant.Outline} isDisabled>
                {t("View form")}
              </Button>
            )}
          </div>
        </li>
        <li>
          <strong>{t("Complete and sign")}</strong>
          <span>{t("Fill in the required details and sign the form.")}</span>
        </li>
        <li>
          <strong>{t("Email your location team")}</strong>
          <span>
            {t(
              "Attach your completed form to an email. Your location team will confirm the next steps."
            )}
          </span>
          <div className="offline-recipient">
            <strong>{location.name}</strong>
            {email == null ? (
              <p>
                {t(
                  "Contact your location team to confirm where to send your completed form."
                )}
              </p>
            ) : (
              <a
                href={`mailto:${encodeURIComponent(
                  email
                )}?subject=${encodeURIComponent(
                  t("Direct Debit setup request")
                )}`}
              >
                {email.split("@")[0]}
                <wbr />@{email.split("@")[1]}
              </a>
            )}
            {email != null && (
              <a
                className="payment-email-action"
                href={`mailto:${encodeURIComponent(
                  email
                )}?subject=${encodeURIComponent(
                  t("Direct Debit setup request")
                )}`}
              >
                {t("Email location team")}
              </a>
            )}
          </div>
        </li>
      </ol>
      <p className="payment-footnote">
        {t(
          "Your location team will confirm when Direct Debit takes effect and help with changes to your details. Sending a form does not change your portal payment methods or Auto-Pay settings."
        )}
      </p>
    </section>
  );
}
